import type { StorageHealthLevel } from "@/platform/types"

// Named thresholds shared by the web and native StorageHealthService
// implementations, so "low" and "critical" mean the same thing everywhere
// regardless of which numbers a given platform can actually report.
export const STORAGE_HEALTH_THRESHOLDS = {
  criticalDeviceFreeBytes: 200 * 1024 * 1024,
  criticalHeadroomBytes: 200 * 1024 * 1024,
  criticalUsageRatio: 0.95,
  lowDeviceFreeBytes: 1 * 1024 * 1024 * 1024,
  lowHeadroomBytes: 500 * 1024 * 1024,
  lowUsageRatio: 0.85,
} as const

export type StorageHealthInputs = {
  usage: number | null
  quota: number | null
  deviceFree: number | null
}

/**
 * Pure threshold check, deliberately taking only the raw numbers rather than
 * a full StorageStatus so it can be unit-tested without touching either
 * platform implementation.
 */
export function deriveStorageHealthLevel({
  usage,
  quota,
  deviceFree,
}: StorageHealthInputs): StorageHealthLevel {
  // A reported quota of 0 isn't a meaningful "no storage at all" signal in
  // practice — treat it the same as an unknown quota rather than reporting a
  // spurious zero headroom.
  const headroom =
    usage !== null && quota !== null && quota > 0 ? quota - usage : null
  const usageRatio =
    usage !== null && quota !== null && quota > 0 ? usage / quota : null

  const isCritical =
    (deviceFree !== null &&
      deviceFree < STORAGE_HEALTH_THRESHOLDS.criticalDeviceFreeBytes) ||
    (headroom !== null &&
      headroom < STORAGE_HEALTH_THRESHOLDS.criticalHeadroomBytes) ||
    (usageRatio !== null &&
      usageRatio >= STORAGE_HEALTH_THRESHOLDS.criticalUsageRatio)
  if (isCritical) return "critical"

  const isLow =
    (deviceFree !== null &&
      deviceFree < STORAGE_HEALTH_THRESHOLDS.lowDeviceFreeBytes) ||
    (headroom !== null &&
      headroom < STORAGE_HEALTH_THRESHOLDS.lowHeadroomBytes) ||
    (usageRatio !== null &&
      usageRatio >= STORAGE_HEALTH_THRESHOLDS.lowUsageRatio)
  if (isLow) return "low"

  return "ok"
}
